import { ChevronRight } from "lucide-react";
import type { TaskListProps } from "../types";
import { cn } from "@/lib/utils";

export function PaperTaskList({ groups, renderTask, empty, footer, listProps }: TaskListProps) {
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);
  const visible = groups.filter((g) => g.tasks.length > 0 || g.showWhenEmpty);
  return (
    <div className="pb-2">
      <div {...listProps} className="outline-none">
        {total === 0 && empty}
        {visible.map((g, i) => (
          <section
            key={g.key}
            className={cn(i > 0 && (g.variant === "day" || g.variant === "range" || g.variant === "month" ? (g.tasks.length ? "mt-8" : "mt-6") : "mt-6"))}
            aria-label={g.variant === "day" ? `${g.dayNumber} ${g.label}` : (g.label ?? undefined)}
          >
            {g.variant === "day" ? (
              <div className="mb-1 flex items-baseline gap-2.5 border-t border-border px-2.5 pt-2.5">
                <span className="font-display text-[24px] font-bold leading-none tracking-[-0.02em] text-foreground tabular">{g.dayNumber}</span>
                <span className="text-[14px] font-medium text-muted-foreground">{g.label}</span>
              </div>
            ) : g.variant === "range" || g.variant === "month" ? (
              <div className="mb-1 border-t border-border px-2.5 pt-2.5">
                <h2 className="text-[18px] font-bold leading-tight tracking-[-0.015em] text-foreground">{g.label}</h2>
              </div>
            ) : g.label !== null && (
              <div className="mb-1 flex h-8 items-center gap-2 border-b border-border/70 px-2.5">
                {g.collapsible ? (
                  <button
                    type="button"
                    onClick={g.onToggle}
                    aria-expanded={!g.collapsed}
                    className="-ml-1 inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-[13.5px] font-semibold text-foreground/85 hover:bg-hover"
                  >
                    <ChevronRight className={cn("size-3.5 text-subtle transition-transform", !g.collapsed && "rotate-90")} />
                    {g.icon}
                    {g.label}
                    <span className="ml-0.5 text-[12px] font-normal text-subtle tabular">{g.tasks.length}</span>
                  </button>
                ) : (
                  <h2 className="inline-flex items-center gap-1.5 font-sans text-[13.5px] font-semibold tracking-normal text-foreground/85">
                    {g.icon}
                    {g.label}
                    <span className="ml-0.5 text-[12px] font-normal text-subtle tabular">{g.tasks.length}</span>
                  </h2>
                )}
                {g.trailing && <div className="ml-auto flex items-center gap-1">{g.trailing}</div>}
              </div>
            )}
            {!g.collapsed && <div className="flex flex-col">{g.tasks.map((t) => renderTask(t))}</div>}
          </section>
        ))}
      </div>
      {footer && <div className="mt-3 px-1">{footer}</div>}
    </div>
  );
}
